/**
 * MyQrPage — standalone "Mi QR" section
 * (DASHBOARD-SIDEMENU-PROFILES-1-FIX2 · Feb 2026).
 *
 * Player-only · displays the player's preview QR + modal. No backend
 * calls. Same mock SVG used inside RacketLabPlayerPreview is reused via
 * the exported `MockQrSvg` component.
 */
import React, { useState } from "react";
import PanelLayout from "@/components/layout/PanelLayout";
import { useLang } from "@/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { QrCode, ShieldCheck } from "lucide-react";
import {
  MockQrSvg,
  QrModal,
  T as RACKET_LAB_T,
} from "@/components/RacketLabPlayerPreview";

const COPY = {
  es: { eyebrow: "Racket Lab", badge: "Preview controlado", title: "Mi QR" },
  en: { eyebrow: "Racket Lab", badge: "Controlled preview", title: "My QR" },
};

export default function MyQrPage() {
  const { user } = useAuth();
  const { lang } = useLang();
  const tx = RACKET_LAB_T[lang] || RACKET_LAB_T.en;
  const copy = COPY[lang] || COPY.en;
  const isPlayer = user?.profile_key === "player";

  const [open, setOpen] = useState(false);

  if (user && !isPlayer) return <Navigate to="/app" replace />;

  return (
    <PanelLayout>
      <div data-testid="page-my-qr">
        <header className="flex flex-wrap items-center gap-3 mb-2">
          <QrCode className="w-6 h-6 text-emerald-700" />
          <div className="tsw-overline">{copy.eyebrow}</div>
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-semibold uppercase tracking-wider"
            data-testid="page-my-qr-badge"
          >
            <ShieldCheck className="w-3 h-3" />{copy.badge}
          </span>
        </header>
        <h1
          className="text-2xl font-bold text-slate-900 mb-6"
          data-testid="page-my-qr-title"
        >
          {copy.title}
        </h1>

        <div
          className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6 flex flex-col sm:flex-row items-center gap-6"
          data-testid="page-my-qr-card"
        >
          <div className="shrink-0">
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="block focus:outline-none focus:ring-2 focus:ring-emerald-400 rounded p-2 bg-white border border-slate-200 shadow-sm"
              data-testid="player-myqr-thumb"
            >
              <MockQrSvg size={220} />
            </button>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <QrCode className="w-4 h-4 text-emerald-700" />
              <h2 className="font-semibold text-slate-900" data-testid="player-myqr-title">{tx.myQrTitle}</h2>
            </div>
            <p className="text-sm text-slate-700">{tx.myQrBody}</p>
            <p className="text-xs text-slate-500 mt-2">{tx.myQrScanHint}</p>
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="mt-4 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
              data-testid="player-myqr-cta"
            >
              {tx.myQrCta}
            </button>
          </div>
        </div>

        <p className="text-[11px] text-slate-500 mt-4 italic">{tx.legalBlurb}</p>

        <QrModal open={open} onClose={() => setOpen(false)} tx={tx} seed="TEAM-PLAYER-DEMO" />
      </div>
    </PanelLayout>
  );
}
